/* eslint-disable react-refresh/only-export-components */
import {
  DimeSelect,
  DimeSelectOptGroup,
  DimeSelectOption,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/select')({
  component: SelectDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

const STATUSES = ['Active', 'Inactive', 'Pending', 'Archived'] as const;

const ACCOUNT_GROUPS: { heading: string; items: string[] }[] = [
  { heading: 'Assets', items: ['Cash', 'Accounts Receivable', 'Inventory'] },
  { heading: 'Liabilities', items: ['Accounts Payable', 'Accrued Liabilities'] },
  { heading: 'Revenue', items: ['Sales Revenue', 'Service Revenue'] },
];

function SelectDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Select</h1>
      <p className="mb-8 text-muted-foreground">
        A native <code className="font-mono text-sm">{'<select>'}</code> element styled to match the
        design system. App forms and filters pick values with{' '}
        <code className="font-mono text-sm">DimeAutocomplete</code> (or{' '}
        <code className="font-mono text-sm">DimeFilterCombobox</code> on a list toolbar) instead,
        even for a short fixed list; this page documents the primitive.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <DimeSelect aria-label="Status" defaultValue="">
            <DimeSelectOption value="" disabled>
              Select status…
            </DimeSelectOption>
            {STATUSES.map((status) => (
              <DimeSelectOption key={status} value={status}>
                {status}
              </DimeSelectOption>
            ))}
          </DimeSelect>
        </Preview>
      </DocsSection>

      <DocsSection title="Trailing adornment">
        <Preview>
          <DimeSelect
            aria-label="Saved status"
            defaultValue="Active"
            trailing={<Check className="size-4 text-emerald-600" aria-hidden="true" />}
          >
            {STATUSES.map((status) => (
              <DimeSelectOption key={status} value={status}>
                {status}
              </DimeSelectOption>
            ))}
          </DimeSelect>
        </Preview>
      </DocsSection>

      <DocsSection title="Sizes">
        <Preview>
          <DimeSelect aria-label="Default size" defaultValue="active">
            <DimeSelectOption value="active">Default</DimeSelectOption>
            <DimeSelectOption value="inactive">Inactive</DimeSelectOption>
          </DimeSelect>
          <DimeSelect size="sm" aria-label="Small size" defaultValue="active">
            <DimeSelectOption value="active">Small</DimeSelectOption>
            <DimeSelectOption value="inactive">Inactive</DimeSelectOption>
          </DimeSelect>
          <DimeSelect size="touch" aria-label="Touch size" defaultValue="active">
            <DimeSelectOption value="active">Touch (44px)</DimeSelectOption>
            <DimeSelectOption value="inactive">Inactive</DimeSelectOption>
          </DimeSelect>
        </Preview>
      </DocsSection>

      <DocsSection title="Option groups">
        <Preview>
          <DimeSelect aria-label="Account" defaultValue="">
            <DimeSelectOption value="" disabled>
              Select account…
            </DimeSelectOption>
            {ACCOUNT_GROUPS.map((group) => (
              <DimeSelectOptGroup key={group.heading} label={group.heading}>
                {group.items.map((item) => (
                  <DimeSelectOption key={item} value={item}>
                    {item}
                  </DimeSelectOption>
                ))}
              </DimeSelectOptGroup>
            ))}
          </DimeSelect>
        </Preview>
      </DocsSection>

      <DocsSection title="Invalid">
        <Preview>
          <DimeSelect aria-label="Status" aria-invalid defaultValue="">
            <DimeSelectOption value="" disabled>
              Select status…
            </DimeSelectOption>
            {STATUSES.map((status) => (
              <DimeSelectOption key={status} value={status}>
                {status}
              </DimeSelectOption>
            ))}
          </DimeSelect>
        </Preview>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <DimeSelect aria-label="Status" disabled defaultValue="active">
            <DimeSelectOption value="active">Active</DimeSelectOption>
            <DimeSelectOption value="inactive">Inactive</DimeSelectOption>
          </DimeSelect>
        </Preview>
      </DocsSection>
    </div>
  );
}
